import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART } from "../../lib/designTokens";
import { fmtDate } from "../../lib/format";
import { getSourceBadgeConfig } from "./ProgressSourceBadge";
import { ProgressChartContainer } from "./ProgressChartContainer";
import { ProgressChartTooltip } from "./ProgressChartTooltip";
import { ProgressSectionState } from "./ProgressSectionStatus";
import {
  POWER_DURATION_SERIES,
  PROGRESS_AXIS_LINE,
  PROGRESS_CHART_GRID_PROPS,
  PROGRESS_CHART_MARGIN,
  PROGRESS_CHART_TICK,
  PROGRESS_Y_AXIS_WIDTH,
  CHART_MIN_HEIGHT,
  buildNumericDomain,
  formatChartDateTick,
  formatTooltipNumber,
  getXAxisInterval,
  lineSeriesDot,
  powerDurationHasChartValues,
} from "./progressChartUtils";

export const ProgressPowerDurationChart = ({ data }) => {
  const hasValues = powerDurationHasChartValues(data);
  const valueKeys = POWER_DURATION_SERIES.map((series) => series.key);
  const domain = buildNumericDomain(data, valueKeys, { includeZero: true });

  if (!hasValues) {
    return (
      <ProgressSectionState
        variant="unavailable"
        title="No usable power values"
        description="Efforts exist for this duration, but measured and estimated power are unavailable."
        testId="power-duration-chart-unavailable"
        className="min-h-72 flex flex-col justify-center app-card-elevated p-4"
      />
    );
  }

  return (
    <div className="app-card-elevated p-4 sm:p-5">
      <ProgressChartContainer
        title="Power-duration chart"
        ariaLabel="Power-duration best efforts by date and source"
        testId="power-duration-chart"
        minHeight={CHART_MIN_HEIGHT.power}
        summary="Power-duration chart with separate measured, smart trainer, estimated, and unknown series."
      >
        <LineChart data={data} margin={PROGRESS_CHART_MARGIN}>
          <CartesianGrid {...PROGRESS_CHART_GRID_PROPS} />
          <XAxis
            dataKey="date"
            tick={PROGRESS_CHART_TICK}
            tickLine={false}
            axisLine={PROGRESS_AXIS_LINE}
            interval={getXAxisInterval(data.length)}
            minTickGap={10}
            tickFormatter={(value, index) => formatChartDateTick(value, index, data.length)}
          />
          <YAxis
            tick={PROGRESS_CHART_TICK}
            tickLine={false}
            axisLine={PROGRESS_AXIS_LINE}
            width={PROGRESS_Y_AXIS_WIDTH}
            domain={domain}
            unit=" W"
          />
          <RechartTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload;
              const rows = [];
              if (row?.bestPowerWatts != null && Number.isFinite(Number(row.bestPowerWatts))) {
                rows.push({
                  label: "Best power",
                  value: formatTooltipNumber(row.bestPowerWatts, { decimals: 0, unit: "W" }),
                });
              }
              if (row?.wattsPerKg != null) {
                rows.push({
                  label: "W/kg",
                  value: formatTooltipNumber(row.wattsPerKg, { decimals: 2, unit: "W/kg" }),
                });
              }
              if (row?.powerSource) {
                rows.push({
                  label: "Source",
                  value: getSourceBadgeConfig(row.powerSource).label,
                });
              }
              return (
                <ProgressChartTooltip
                  active
                  payload={payload}
                  label={row?.date ? fmtDate(row.date) : null}
                  rows={rows}
                />
              );
            }}
          />
          {POWER_DURATION_SERIES.map((series) => (
            <Line
              key={series.key}
              type="monotone"
              dataKey={series.key}
              name={series.label}
              stroke={series.color}
              strokeWidth={2.25}
              dot={lineSeriesDot(data.length, series.color)}
              activeDot={{ r: 5, fill: series.color, stroke: CHART.dotStroke, strokeWidth: 1.5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ProgressChartContainer>
    </div>
  );
};
